<template>
    <BaseView>
        <section class="preferences">
            <div class="hero">
                <div>
                    <p class="eyebrow">Preferences</p>
                    <h1>Email</h1>
                    <p class="muted">Stay in control of how often we reach your inbox.</p>
                    <div class="hero__tags">
                        <Tag value="Email updates" severity="info" />
                        <Tag value="Weekly digest" :icon="weeklyDigest ? 'pi pi-check' : 'pi pi-times'" :severity="weeklyDigest ? 'success' : 'danger'" />
                        <Tag v-if="hasManageUsersPermission" value="User changes" :icon="userChanges ? 'pi pi-check' : 'pi pi-times'" :severity="userChanges ? 'success' : 'danger'" />
                    </div>
                </div>
            </div>

            <div class="cards">
                <Card class="pref-card">
                    <template #title>
                        <div class="card__title">
                            <div>
                                <p class="label">Weekly digest</p>
                                <p class="muted">New graph versions you have access to</p>
                            </div>
                            <ToggleSwitch v-model="weeklyDigest" inputId="weeklyDigest" aria-label="Toggle weekly digest" />
                        </div>
                    </template>
                    <template #content>
                        <ul class="detail-list">
                            <li>
                                <i class="pi pi-check"></i>
                                <span>List of all new graph versions available.</span>
                            </li>
                            <li>
                                <i class="pi pi-check"></i>
                                <span>Detailed descriptions of changes in each version.</span>
                            </li>
                            <li>
                                <i class="pi pi-check"></i>
                                <span>Quick access to updated graphs you own or collaborate on.</span>
                            </li>
                        </ul>
                    </template>
                    <template #footer>
                        <div class="card__footer">
                            <div class="footer__left">
                                <i class="pi pi-envelope"></i>
                                <span>Delivery window: Monday mornings.</span>
                            </div>
                            <Tag :value="weeklyDigest ? 'Enabled' : 'Disabled'" :severity="weeklyDigest ? 'success' : 'danger'" />
                        </div>
                    </template>
                </Card>

                <Card class="pref-card" v-if="hasManageUsersPermission">
                    <template #title>
                        <div class="card__title">
                            <div>
                                <p class="label">User changes</p>
                                <p class="muted">Users added/removed from this instance</p>
                            </div>
                            <ToggleSwitch v-model="userChanges" inputId="userChanges" aria-label="Toggle user changes" />
                        </div>
                    </template>
                    <template #content>
                        <ul class="detail-list">
                            <li>
                                <i class="pi pi-check"></i>
                                <span>Weekly summary of users added.</span>
                            </li>
                            <li>
                                <i class="pi pi-check"></i>
                                <span>Weekly summary of users removed or deactivated.</span>
                            </li>
                            <li>
                                <i class="pi pi-check"></i>
                                <span>At-a-glance counts and direct links for review.</span>
                            </li>
                        </ul>
                    </template>
                    <template #footer>
                        <div class="card__footer">
                            <div class="footer__left">
                                <i class="pi pi-envelope"></i>
                                <span>Delivery window: Monday mornings.</span>
                            </div>
                            <Tag :value="userChanges ? 'Enabled' : 'Disabled'" :severity="userChanges ? 'success' : 'danger'" />
                        </div>
                    </template>
                </Card>
            </div>
        </section>
    </BaseView>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import BaseView from '../BaseView.vue';
import { useStore } from 'vuex';

const store = useStore();
const weeklyDigest = computed({
    get: () => store.getters['userStore/preferences'].email?.weekly_digest,
    set: (value) => {
        store.dispatch('userStore/updateUserPreference', { preference: 'email.weekly_digest', value });
    }
});

const userChanges = computed({
    get: () => store.getters['userStore/preferences'].email?.user_changes,
    set: (value) => {
        store.dispatch('userStore/updateUserPreference', { preference: 'email.user_changes', value });
    }
});

const hasManageUsersPermission = computed(() => {
    const perms = store.getters['userStore/globalPermissions']
    return perms && Object.prototype.hasOwnProperty.call(perms, 'manage_users')
})

// Fetch user preferences on component mount
onMounted(() => {
    store.dispatch('userStore/loadUserPreferences')
    // Ensure global permissions are available for gating the User changes section
    store.dispatch('userStore/loadGlobalPermissions')
});

</script>

<style scoped>
.preferences {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 960px;
    margin: 0 auto;
}

.hero {
    background: linear-gradient(135deg, #eef2ff, #f8fafc);
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 1.5rem 1.75rem;
    box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.hero__tags {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-top: 0.75rem;
}

.hero__tags :deep(.p-tag) {
    align-items: baseline !important;
}

.preferences h1 {
    margin: 0.2rem 0;
    font-size: 1.8rem;
    color: #0f172a;
}

.eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.8rem;
    color: #6b7280;
    margin: 0;
}

.muted {
    color: #6b7280;
    margin: 0.1rem 0 0;
}

.cards {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
}

.pref-card :deep(.p-card-body) {
    padding: 1.4rem 1.6rem 1.1rem;
}

.card__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.label {
    margin: 0;
    font-weight: 700;
    font-size: 1.1rem;
    color: #0f172a;
}

.detail-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 0.4rem;
}

.detail-list li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #111827;
}

.detail-list i {
    color: #16a34a;
    font-size: 0.95rem;
}

.card__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 0.25rem;
}

.footer__left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 0.9rem;
}

@media (max-width: 640px) {
    .card__title {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>